import {
  BlueprintStoreyAssignment,
  BlueprintStoreyAssignmentKind,
  BlueprintUnderlay,
  LegacyBlueprintStoreyAssignment,
  PersistedBlueprintStoreyAssignment,
} from '@shared/models/design/blueprint-underlay.model';
import type { Storey } from '@shared/models/design/storey.model';
import { assertUnreachable } from '../function.helpers';
import { guards } from '../typeguards.helpers';

/** Minimal storey shape needed to expand semantic assignments. */
export type StoreyForAssignment = Pick<
  Storey,
  'id' | 'elevation' | 'mezzanine'
>;

/** Higher = more specific when multiple pages cover the same level. */
const ASSIGNMENT_SPECIFICITY: Record<BlueprintStoreyAssignmentKind, number> = {
  otherStoreys: 3,
  groundFloor: 3,
  topFloor: 3,
  basementGarage: 3,
  typicalStoreys: 2,
  allStoreys: 1,
};

const ASSIGNMENT_LABEL: Record<BlueprintStoreyAssignmentKind, string> = {
  allStoreys: 'All storeys',
  groundFloor: 'Ground storey',
  typicalStoreys: 'Typical storeys',
  topFloor: 'Top storey',
  basementGarage: 'Basement / garage',
  otherStoreys: 'Other storeys',
};

/**
 * True when a persisted assignment is the pre-kind `{ fromLevel, toLevel }` shape.
 */
export const isLegacyStoreyAssignment = (
  assignment: PersistedBlueprintStoreyAssignment | undefined,
): assignment is LegacyBlueprintStoreyAssignment =>
  !!assignment &&
  !('kind' in assignment) &&
  guards.is.number(assignment.fromLevel) &&
  guards.is.number(assignment.toLevel);

/**
 * Inclusive 1-based levels from a from–to range.
 */
export const levelsFromBlueprintStoreyRange = (
  fromLevel: number,
  toLevel: number,
): number[] => {
  const from = Math.max(1, Math.floor(fromLevel));
  const to = Math.max(from, Math.floor(toLevel));
  const levels: number[] = [];
  for (let level = from; level <= to; level++) levels.push(level);
  return levels;
};

/**
 * Explicit otherStoreys assignment covering an inclusive level range.
 */
export const blueprintStoreyAssignmentFromLevelRange = (
  fromLevel: number,
  toLevel: number,
): BlueprintStoreyAssignment => ({
  kind: 'otherStoreys',
  levels: levelsFromBlueprintStoreyRange(fromLevel, toLevel),
});

/**
 * Normalize unique sorted 1-based levels for {@link BlueprintStoreyAssignment}.
 */
export const normalizeBlueprintStoreyLevels = (
  levels: readonly number[],
): number[] => {
  const unique = new Set<number>();
  for (const raw of levels) {
    if (!Number.isFinite(raw)) continue;
    const level = Math.floor(raw);
    if (level < 1) continue;
    unique.add(level);
  }
  return [...unique].sort((a, b) => a - b);
};

/**
 * Normalize persisted or draft assignment into the current discriminated shape.
 */
export const normalizeBlueprintStoreyAssignment = (
  assignment: PersistedBlueprintStoreyAssignment | undefined,
  fallbackLevel = 1,
): BlueprintStoreyAssignment => {
  if (!assignment) {
    return {
      kind: 'otherStoreys',
      levels: [Math.max(1, Math.floor(fallbackLevel))],
    };
  }
  if (isLegacyStoreyAssignment(assignment)) {
    return {
      kind: 'otherStoreys',
      levels: levelsFromBlueprintStoreyRange(
        assignment.fromLevel,
        assignment.toLevel,
      ),
    };
  }
  switch (assignment.kind) {
    case 'allStoreys':
    case 'groundFloor':
    case 'typicalStoreys':
    case 'topFloor':
    case 'basementGarage':
      return { kind: assignment.kind };
    case 'otherStoreys': {
      const levels = normalizeBlueprintStoreyLevels(assignment.levels);
      return {
        kind: 'otherStoreys',
        levels: levels.length
          ? levels
          : [Math.max(1, Math.floor(fallbackLevel))],
      };
    }
    default:
      return assertUnreachable(assignment);
  }
};

/**
 * True when two normalized assignments are equal.
 */
export const areBlueprintStoreyAssignmentsEqual = (
  left: BlueprintStoreyAssignment,
  right: BlueprintStoreyAssignment,
): boolean => {
  if (left.kind !== right.kind) return false;
  if (left.kind !== 'otherStoreys' || right.kind !== 'otherStoreys')
    return true;
  if (left.levels.length !== right.levels.length) return false;
  return left.levels.every((level, index) => level === right.levels[index]);
};

/**
 * Short UI label for an assignment kind (or explicit storey list).
 */
export const formatBlueprintStoreyAssignmentLabel = (
  assignment: BlueprintStoreyAssignment,
): string => {
  if (assignment.kind !== 'otherStoreys') {
    return ASSIGNMENT_LABEL[assignment.kind];
  }
  const { levels } = assignment;
  if (!levels.length) return ASSIGNMENT_LABEL.otherStoreys;
  if (levels.length === 1) return `Storey ${levels[0]}`;
  return `Storeys ${levels.join(', ')}`;
};

/**
 * Specificity rank used when multiple pages cover the same storey level.
 */
export const getBlueprintStoreyAssignmentSpecificity = (
  assignment: BlueprintStoreyAssignment,
): number => ASSIGNMENT_SPECIFICITY[assignment.kind];

/**
 * 1-based levels of non-mezzanine storeys (array order = bottom → top).
 */
const getNonMezzanineLevels = (
  storeys: readonly StoreyForAssignment[],
): number[] => {
  const levels: number[] = [];
  for (let index = 0; index < storeys.length; index++) {
    if (storeys[index]?.mezzanine) continue;
    levels.push(index + 1);
  }
  return levels;
};

/**
 * Expand a semantic assignment to concrete 1-based levels for the current stack.
 */
export const expandBlueprintStoreyAssignment = (
  assignment: BlueprintStoreyAssignment,
  storeys: readonly StoreyForAssignment[],
): number[] => {
  const nonMezz = getNonMezzanineLevels(storeys);
  const fallbackLevels =
    nonMezz.length > 0
      ? nonMezz
      : storeys.length > 0
        ? storeys.map((_, index) => index + 1)
        : [1];

  switch (assignment.kind) {
    case 'allStoreys':
      return fallbackLevels;
    case 'groundFloor':
      return [fallbackLevels[0] ?? 1];
    case 'topFloor':
      return [fallbackLevels[fallbackLevels.length - 1] ?? 1];
    case 'basementGarage': {
      // Below-grade ground (`elevation < 0`); otherwise behave like ground.
      const ground = storeys.find((storey) => !storey.mezzanine) ?? storeys[0];
      if (ground && (ground.elevation ?? 0) < 0) {
        const index = storeys.indexOf(ground);
        return [index >= 0 ? index + 1 : 1];
      }
      return [fallbackLevels[0] ?? 1];
    }
    case 'typicalStoreys': {
      if (fallbackLevels.length <= 2) return [];
      return fallbackLevels.slice(1, -1);
    }
    case 'otherStoreys':
      return normalizeBlueprintStoreyLevels(assignment.levels);
    default:
      return assertUnreachable(assignment);
  }
};

/**
 * Min/max 1-based span for a single assignment’s expanded levels
 * (legacy ranges / semantic kinds). Prefer
 * {@link resolveBlueprintUnderlayStoreySpans} for page→storey UI coverage.
 */
export const getBlueprintStoreyAssignmentLevelSpan = (
  assignment: BlueprintStoreyAssignment,
  storeys: readonly StoreyForAssignment[] = [],
): { fromLevel: number; toLevel: number } => {
  const levels = expandBlueprintStoreyAssignment(assignment, storeys);
  if (!levels.length) return { fromLevel: 1, toLevel: 1 };
  return {
    fromLevel: levels[0]!,
    toLevel: levels[levels.length - 1]!,
  };
};

/** Max level offered in Storey dropdowns (import step 2 + PDF panel). */
export const BLUEPRINT_STOREY_LEVEL_SELECT_MAX = 15;

/**
 * UI label for a 1-based storey level (`Storey 1`).
 */
export const formatBlueprintStoreyLevelLabel = (level: number): string =>
  `Storey ${Math.max(1, Math.floor(level))}`;

/**
 * Start storey for a page assignment — the level the user picks in the Storey
 * dropdown. Coverage then runs until the next page’s start
 * ({@link resolveBlueprintUnderlayStoreySpans}).
 */
export const getBlueprintStoreyAssignmentStartLevel = (
  assignment: BlueprintStoreyAssignment,
  storeys: readonly StoreyForAssignment[] = [],
): number =>
  getBlueprintStoreyAssignmentLevelSpan(assignment, storeys).fromLevel;

/**
 * Inclusive storey span each underlay covers — exactly the levels it is
 * assigned to, never the floors above. A page placed on Storey 1 stays on
 * Storey 1, so adding a storey leaves that new storey without an underlay;
 * put the same drawing on several floors with "Copy to storey" instead.
 */
export const resolveBlueprintUnderlayStoreySpans = (
  underlays: readonly BlueprintUnderlay[],
  storeys: readonly StoreyForAssignment[] = [],
): ReadonlyMap<string, { fromLevel: number; toLevel: number }> => {
  const spans = new Map<string, { fromLevel: number; toLevel: number }>();
  for (const underlay of underlays) {
    spans.set(
      underlay.id,
      getBlueprintStoreyAssignmentLevelSpan(underlay.storeyAssignment, storeys),
    );
  }
  return spans;
};

/**
 * Single-level assignment for the Storey dropdown (`Storey N`).
 */
export const blueprintStoreyAssignmentFromStartLevel = (
  startLevel: number,
): BlueprintStoreyAssignment => {
  const level = Math.max(1, Math.floor(startLevel));
  return { kind: 'otherStoreys', levels: [level] };
};

/**
 * True when two assignments cover any shared storey level.
 */
export const blueprintStoreyAssignmentsOverlap = (
  left: BlueprintStoreyAssignment,
  right: BlueprintStoreyAssignment,
  storeys: readonly StoreyForAssignment[] = [],
): boolean => {
  const leftLevels = new Set(expandBlueprintStoreyAssignment(left, storeys));
  for (const level of expandBlueprintStoreyAssignment(right, storeys)) {
    if (leftLevels.has(level)) return true;
  }
  return false;
};

/**
 * UI storey count for PDF From/To editors (`storeys[0]` → level 1).
 */
export const getBlueprintStoreyStackSize = (
  storeys: readonly StoreyForAssignment[],
): number => Math.max(1, storeys.length);

/**
 * Clamp an inclusive 1-based From–To range into `1…maxLevel`.
 */
export const clampBlueprintStoreyLevelRange = (
  fromLevel: number,
  toLevel: number,
  maxLevel: number,
): { fromLevel: number; toLevel: number } => {
  const max = Math.max(1, Math.floor(maxLevel));
  const from = Math.min(max, Math.max(1, Math.floor(fromLevel)));
  const to = Math.min(max, Math.max(from, Math.floor(toLevel)));
  return { fromLevel: from, toLevel: to };
};

/**
 * Remove a claimed inclusive range from another inclusive range.
 * When the claim splits the range, keep the larger remaining segment
 * (lower wins ties) so the From–To UI stays a single contiguous span.
 */
export const subtractBlueprintStoreyLevelRange = (
  fromLevel: number,
  toLevel: number,
  claimFrom: number,
  claimTo: number,
): { fromLevel: number; toLevel: number } | undefined => {
  if (toLevel < claimFrom || fromLevel > claimTo) {
    return { fromLevel, toLevel };
  }
  const belowTo = claimFrom - 1;
  const aboveFrom = claimTo + 1;
  const below =
    fromLevel <= belowTo
      ? { fromLevel, toLevel: Math.min(toLevel, belowTo) }
      : undefined;
  const above =
    aboveFrom <= toLevel
      ? { fromLevel: Math.max(fromLevel, aboveFrom), toLevel }
      : undefined;
  if (below && above) {
    const belowSize = below.toLevel - below.fromLevel + 1;
    const aboveSize = above.toLevel - above.fromLevel + 1;
    return belowSize >= aboveSize ? below : above;
  }
  return below ?? above;
};

/**
 * Apply a page’s storey claim: clamp to the building stack, then shrink any
 * overlapping pages so they no longer include those levels (never invents
 * levels above the top storey).
 */
export const applyBlueprintStoreyClaimToUnderlays = (
  underlays: readonly BlueprintUnderlay[],
  claimingId: string,
  claimFrom: number,
  claimTo: number,
  storeys: readonly StoreyForAssignment[],
): BlueprintUnderlay[] => {
  const maxLevel = getBlueprintStoreyStackSize(storeys);
  const { fromLevel, toLevel } = clampBlueprintStoreyLevelRange(
    claimFrom,
    claimTo,
    maxLevel,
  );
  const claimAssignment = blueprintStoreyAssignmentFromLevelRange(
    fromLevel,
    toLevel,
  );
  const claimed = new Set(levelsFromBlueprintStoreyRange(fromLevel, toLevel));

  return underlays.map((entry) => {
    if (entry.id === claimingId) {
      return { ...entry, storeyAssignment: claimAssignment };
    }
    if (
      !blueprintStoreyAssignmentsOverlap(
        claimAssignment,
        entry.storeyAssignment,
        storeys,
      )
    ) {
      return entry;
    }

    const span = getBlueprintStoreyAssignmentLevelSpan(
      entry.storeyAssignment,
      storeys,
    );
    const clamped = clampBlueprintStoreyLevelRange(
      span.fromLevel,
      span.toLevel,
      maxLevel,
    );
    const remaining = subtractBlueprintStoreyLevelRange(
      clamped.fromLevel,
      clamped.toLevel,
      fromLevel,
      toLevel,
    );
    if (!remaining) {
      let free: number | undefined;
      for (let level = 1; level <= maxLevel; level++) {
        if (!claimed.has(level)) {
          free = level;
          break;
        }
      }
      if (free === undefined) return entry;
      return {
        ...entry,
        storeyAssignment: blueprintStoreyAssignmentFromLevelRange(free, free),
      };
    }
    return {
      ...entry,
      storeyAssignment: blueprintStoreyAssignmentFromLevelRange(
        remaining.fromLevel,
        remaining.toLevel,
      ),
    };
  });
};
